import { Directive, ElementRef, HostListener, inject } from '@angular/core';

const CHARACTER_MAP: Record<string, string> = {
  // Accented vowels
  'á': 'a', 'à': 'a', 'â': 'a', 'ä': 'a', 'ã': 'a',
  'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e',
  'í': 'i', 'ì': 'i', 'î': 'i', 'ï': 'i',
  'ó': 'o', 'ò': 'o', 'ô': 'o', 'ö': 'o', 'õ': 'o',
  'ú': 'u', 'ù': 'u', 'û': 'u', 'ü': 'u',
  'ý': 'y',
  'Á': 'A', 'À': 'A', 'Â': 'A', 'Ä': 'A', 'Ã': 'A',
  'É': 'E', 'È': 'E', 'Ê': 'E', 'Ë': 'E',
  'Í': 'I', 'Ì': 'I', 'Î': 'I', 'Ï': 'I',
  'Ó': 'O', 'Ò': 'O', 'Ô': 'O', 'Ö': 'O', 'Õ': 'O',
  'Ú': 'U', 'Ù': 'U', 'Û': 'U', 'Ü': 'U',
  'Ý': 'Y',

  // Western European
  'ñ': 'n', 'Ñ': 'N',
  'ç': 'c', 'Ç': 'C',
  'ß': 'ss',

  // Ligatures & Nordic
  'æ': 'ae', 'Æ': 'AE',
  'œ': 'oe', 'Œ': 'OE',
  'ø': 'oe', 'Ø': 'OE',
  'å': 'ae', 'Å': 'AE',

  // Icelandic
  'ð': 'd', 'Ð': 'D',
  'þ': 'th', 'Þ': 'TH',

  // Polish
  'ł': 'l', 'Ł': 'L',
  'ś': 's', 'Ś': 'S',
  'ć': 'c', 'Ć': 'C',
  'ź': 'z', 'Ź': 'Z',
  'ż': 'z', 'Ż': 'Z',
  'ń': 'n', 'Ń': 'N',
  'ą': 'a', 'Ą': 'A',
  'ę': 'e', 'Ę': 'E',

  // South Slavic
  'š': 's', 'Š': 'S',
  'ž': 'z', 'Ž': 'Z',
  'č': 'c', 'Č': 'C',

  // Turkish
  'ğ': 'g', 'Ğ': 'G',
  'ş': 's', 'Ş': 'S',
  'ı': 'i', 'İ': 'i',

  // Czech
  'ř': 'r', 'Ř': 'R',
  'ě': 'e', 'Ě': 'E',

  // Hungarian
  'ő': 'o', 'Ő': 'O',
  'ű': 'u', 'Ű': 'U',

  // Romanian
  'ă': 'a', 'Ă': 'A',
  'ș': 's', 'Ș': 'S',
  'ț': 't', 'Ț': 'T',
};

@Directive({
  selector: 'input[appTextNormalize], textarea[appTextNormalize]',
  standalone: true,
})
export class TextNormalizeDirective {
  private el = inject(ElementRef<HTMLInputElement>);

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text') ?? '';
    if (!text) return;

    const normalized = this.normalize(text);
    if (normalized === text) return;

    event.preventDefault();

    const input = this.el.nativeElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const current = input.value;
    input.value = current.slice(0, start) + normalized + current.slice(end);

    const newPos = start + normalized.length;
    input.setSelectionRange(newPos, newPos);

    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private normalize(text: string): string {
    let result = '';
    for (const ch of text) {
      result += CHARACTER_MAP[ch] ?? ch;
    }
    return result;
  }
}
